import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, SunIcon, MoonIcon } from './Icons';
import { STUB_PLAYER, COSMETICS_CONFIG, xpToLevel } from '../gamificationConfig';
import { authHeaders } from '../authToken';
import { accountPlan } from '../curriculumConfig';

// Colours for the account-plan pill, keyed by accountPlan().key. Premium reuses the
// purple used by the content locks/upsell so the tiers read as one system.
const PLAN_TONES = {
  free:    { bg: 'var(--surface-muted, #eee)',    fg: 'var(--text-muted, #666)', bd: 'var(--border, #ddd)' },
  premium: { bg: 'oklch(0.62 0.19 300 / 0.12)',   fg: 'oklch(0.52 0.19 300)',    bd: 'oklch(0.62 0.19 300 / 0.4)' },
  teacher: { bg: 'oklch(0.7 0.12 190 / 0.16)',    fg: 'oklch(0.46 0.1 200)',     bd: 'oklch(0.7 0.12 190 / 0.45)' },
  staff:   { bg: 'oklch(0.75 0.14 70 / 0.18)',    fg: 'oklch(0.5 0.13 60)',      bd: 'oklch(0.75 0.14 70 / 0.45)' },
  school:  { bg: 'oklch(0.65 0.15 250 / 0.14)',   fg: 'oklch(0.48 0.15 255)',    bd: 'oklch(0.65 0.15 250 / 0.45)' },
};

function MenuIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
      xmlns="http://www.w3.org/2000/svg">
      <line x1="2" y1="4" x2="14" y2="4" />
      <line x1="2" y1="8" x2="14" y2="8" />
      <line x1="2" y1="12" x2="14" y2="12" />
    </svg>
  );
}

function StarIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor"
      xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M8 1l1.85 3.75 4.15.6-3 2.93.71 4.14L8 10.27l-3.71 1.95.71-4.14L2 4.85l4.15-.6L8 1z" />
    </svg>
  );
}

export default function Topbar({
  exercise,
  theme,
  onToggleTheme,
  accent = 'violet',
  onAccentChange,
  progress,
  canAdvance,
  onNext,
  onPrevious,
  currentTaskIndex,
  totalTasks,
  onOpenDashboard,
  onOpenDrawer,
  playerData,
  unitExercises = [],
  currentExerciseId,
  isDashboard = false,
  onLogout,
  displayName = '',
  onDisplayNameChange,
}) {
  const completedCount = progress?.completed_count || 0;
  const dotCount       = totalTasks ?? 8;

  // Use live DB value when available; fall back to stub while loading.
  // The avatar level is the shared combined pool (both themes); the XP pill
  // shows that same total so the level and number always agree.
  const lifetimeXP     = playerData?.combinedXP ?? playerData?.globalXP ?? playerData?.lifetimeXP ?? STUB_PLAYER.lifetimeXP;
  const level          = xpToLevel(lifetimeXP);
  const equippedRing   = COSMETICS_CONFIG.rings.find(r => r.id === (playerData?.equippedRingId ?? STUB_PLAYER.equippedRingId))
    ?? COSMETICS_CONFIG.rings[0];
  const equippedAvatar = COSMETICS_CONFIG.avatars.find(a => a.id === (playerData?.equippedAvatarId ?? STUB_PLAYER.equippedAvatarId))
    ?? COSMETICS_CONFIG.avatars[0];
  const equippedPet    = COSMETICS_CONFIG.pets.find(p => p.id === (playerData?.equippedPetId ?? STUB_PLAYER.equippedPetId))
    ?? COSMETICS_CONFIG.pets[0];

  const ringStyle = equippedRing.cssColor
    ? { boxShadow: `0 0 0 2px var(--bg), 0 0 0 4px ${equippedRing.cssColor}` }
    : {};

  // ── Profile dropdown ─────────────────────────────────────────────────────

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [dropdownOpen]);

  function handleLogout() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('student_id');
    localStorage.removeItem('account_type');
    localStorage.removeItem('display_name');
    if (onLogout) {
      onLogout();
    } else {
      window.location.reload();
    }
  }

  const handleResetProgress = async () => {
    if (!window.confirm('⚠️ DEV WARNING: Are you sure you want to completely wipe all XP, badges, and progress for this account? This cannot be undone.')) return;
    try {
      // Self-service reset: the backend derives the account from the bearer
      // token, so this only ever wipes the caller's own progress.
      await fetch('/api/account/reset', { method: 'POST', headers: authHeaders() });
      window.location.href = '/'; // Force a full hard reload to clear React state
    } catch (err) {
      console.error("Reset failed", err);
      alert("Failed to reset progress.");
    }
  };

  // ── Display name editing ─────────────────────────────────────────────────

  const resolvedName = displayName || localStorage.getItem('display_name') || 'Student';
  const [nameInput,    setNameInput]    = useState(resolvedName);
  const [nameSaving,   setNameSaving]   = useState(false);
  const [nameMsg,      setNameMsg]      = useState('');

  // Keep local input in sync if prop changes (e.g. after login)
  useEffect(() => {
    setNameInput(displayName || localStorage.getItem('display_name') || 'Student');
  }, [displayName]);

  const handleSaveName = useCallback(async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === resolvedName) return;
    setNameSaving(true);
    setNameMsg('');
    try {
      const res = await fetch('/api/user/update-name', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ new_name: trimmed }),
      });
      // Guard against non-JSON responses (e.g. HTML error pages in dev)
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Server returned ${res.status} (${res.statusText || 'non-JSON response'})`);
      }
      const data = await res.json();
      if (data.success) {
        setNameMsg('✓ Saved');
        if (onDisplayNameChange) onDisplayNameChange(data.display_name);
      } else {
        setNameMsg(data.reason || 'Error saving');
      }
    } catch (err) {
      setNameMsg(err.message || 'Network error');
    } finally {
      setNameSaving(false);
      setTimeout(() => setNameMsg(''), 3000);
    }
  }, [nameInput, resolvedName, onDisplayNameChange]);

  // ── Appearance (accent colour) ───────────────────────────────────────────

  const [accentOpen, setAccentOpen] = useState(false);
  const ACCENTS = [
    { id: 'violet', label: 'Violet', swatch: 'oklch(0.68 0.16 295)' },
    { id: 'sage',   label: 'Sage',   swatch: 'oklch(0.62 0.12 155)' },
    { id: 'amber',  label: 'Amber',  swatch: 'oklch(0.68 0.15 55)'  },
    { id: 'ocean',  label: 'Ocean',  swatch: 'oklch(0.60 0.15 250)' },
  ];

  // ── Change password ──────────────────────────────────────────────────────

  const [pwOpen,     setPwOpen]     = useState(false);
  const [pwCurrent,  setPwCurrent]  = useState('');
  const [pwNew,      setPwNew]      = useState('');
  const [pwConfirm,  setPwConfirm]  = useState('');
  const [pwSaving,   setPwSaving]   = useState(false);
  const [pwMsg,      setPwMsg]      = useState('');

  const resetPwForm = () => {
    setPwCurrent(''); setPwNew(''); setPwConfirm(''); setPwMsg('');
  };

  const handleChangePassword = useCallback(async () => {
    if (pwNew.length < 8) { setPwMsg('New password must be at least 8 characters.'); return; }
    if (pwNew !== pwConfirm) { setPwMsg('New passwords do not match.'); return; }
    setPwSaving(true);
    setPwMsg('');
    try {
      const res = await fetch('/api/account/change-password', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ current_password: pwCurrent, new_password: pwNew }),
      });
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Server returned ${res.status} (${res.statusText || 'non-JSON response'})`);
      }
      const data = await res.json();
      if (data.success) {
        setPwMsg('✓ Password updated');
        setPwCurrent(''); setPwNew(''); setPwConfirm('');
        setTimeout(() => { setPwOpen(false); setPwMsg(''); }, 1500);
      } else {
        setPwMsg(data.reason || 'Could not change password.');
      }
    } catch (err) {
      setPwMsg(err.message || 'Network error');
    } finally {
      setPwSaving(false);
    }
  }, [pwCurrent, pwNew, pwConfirm]);

  const tierLabel = level >= 10 ? '🏆 Gold Mastery' : level >= 5 ? '🥈 Silver' : '⚡ Rising';

  // Account plan (subscription status) — distinct from the gamification Tier above.
  const plan     = accountPlan(playerData);
  const planTone = PLAN_TONES[plan.key] || PLAN_TONES.free;

  const pwInputStyle = {
    width: '100%', padding: '6px 8px', fontSize: '13px', marginBottom: '6px',
    border: '1px solid var(--border, #ddd)', borderRadius: '6px',
    background: 'var(--surface, #fff)', color: 'var(--text, #111)',
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <header className="topbar">

      {/* Brand block */}
      <div className="topbar-brand">
        <button
          className="topbar-menu-btn"
          onClick={onOpenDrawer}
          aria-label="Open curriculum navigation"
          title="Browse curriculum"
        >
          <MenuIcon />
        </button>

        <button
          className="brand-home-btn"
          onClick={onOpenDashboard}
          aria-label="Go to dashboard"
          title="Go to dashboard"
        >
          <span className="brand-logo" aria-hidden="true">ib</span>
          <span className="brand-text">
            <span className="brand-title">Computer Science</span>
          </span>
        </button>
      </div>

      {/* Centre: nav pill + progress dots (hidden on dashboard) */}
      <div className="topbar-centre">
        {isDashboard ? null : (
          <>
            <div className="lesson-nav-pill">
              <button
                className="nav-chevron"
                onClick={currentTaskIndex > 0 ? onPrevious : undefined}
                disabled={!(currentTaskIndex > 0)}
                aria-label="Previous exercise"
              >
                <ChevronLeftIcon />
              </button>

              <span className="nav-label">
                {exercise ? (
                  <>
                    <strong className="nav-id">{exercise.id}</strong>
                    <span className="nav-sep"> · </span>
                    <span className="nav-exercise-title">{exercise.title}</span>
                  </>
                ) : (
                  <span className="nav-exercise-title">Loading…</span>
                )}
              </span>

              <button
                className="nav-chevron"
                onClick={canAdvance ? onNext : undefined}
                disabled={!canAdvance}
                aria-label="Next exercise"
              >
                <ChevronRightIcon />
              </button>
            </div>

            <div className="progress-dots" aria-label={`${completedCount} of ${unitExercises.length || dotCount} completed`}>
              {unitExercises.length > 0 ? (
                unitExercises.map((ex) => {
                  const isCurrent   = ex.id === currentExerciseId;
                  const isCompleted = progress?.completed_exercises?.includes(ex.id);
                  return (
                    <div
                      key={ex.id}
                      title={`${ex.id} (${ex.type})`}
                      className={
                        isCurrent   ? 'progress-dot dot-current' :
                        isCompleted ? 'progress-dot dot-done'    :
                                      'progress-dot dot-future'
                      }
                    />
                  );
                })
              ) : (
                Array.from({ length: dotCount }, (_, i) => (
                  <div
                    key={i}
                    className={
                      i === currentTaskIndex ? 'progress-dot dot-current' :
                      i < completedCount     ? 'progress-dot dot-done'    :
                                               'progress-dot dot-future'
                    }
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Right: XP pill + theme toggle + user */}
      <div className="topbar-right">

        {/* XP / Level pill — opens dashboard */}
        <button className="xp-pill" onClick={onOpenDashboard} aria-label="Open competency dashboard">
          <StarIcon />
          <span className="xp-pill-level">Lvl {level}</span>
          <span className="xp-pill-sep" aria-hidden="true">·</span>
          <span className="xp-pill-xp">{lifetimeXP.toLocaleString()} XP</span>
        </button>

        <div className="topbar-sep" aria-hidden="true" />

        <button
          className="icon-btn"
          onClick={onToggleTheme}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>

        <div className="topbar-sep" aria-hidden="true" />

        {/* Avatar with dropdown */}
        <div className="user-block" ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            className="user-block-btn"
            onClick={() => setDropdownOpen(o => !o)}
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
              borderRadius: '10px',
              outline: dropdownOpen ? '2px solid var(--accent, #6366f1)' : 'none',
            }}
          >
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div
                className={`user-avatar topbar-avatar-emoji${equippedRing.id === 'ring_prism' ? ' topbar-ring-prism' : ''}`}
                style={ringStyle}
              >
                <span role="img" aria-label={equippedAvatar.label}>{equippedAvatar.emoji}</span>
              </div>
              {equippedPet.id !== 'pet_none' && (
                <span
                  style={{
                    position: 'absolute', bottom: -2, right: -4,
                    fontSize: '10px', lineHeight: 1, pointerEvents: 'none',
                  }}
                  role="img" aria-label={equippedPet.label}
                >
                  {equippedPet.emoji}
                </span>
              )}
            </div>
            <div className="user-info">
              <span className="user-name">{resolvedName}</span>
            </div>
          </button>

          {/* Dropdown panel */}
          {dropdownOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                minWidth: '220px', zIndex: 9999,
                background: 'var(--surface, #fff)',
                border: '1px solid var(--border, #e5e7eb)',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                overflow: 'hidden',
              }}
            >
              {/* Header strip */}
              <div style={{
                padding: '14px 16px 12px',
                borderBottom: '1px solid var(--border, #e5e7eb)',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div className="user-avatar topbar-avatar-emoji" style={ringStyle}>
                    <span role="img" aria-label={equippedAvatar.label}>{equippedAvatar.emoji}</span>
                  </div>
                  {equippedPet.id !== 'pet_none' && (
                    <span
                      style={{
                        position: 'absolute', bottom: -2, right: -4,
                        fontSize: '10px', lineHeight: 1, pointerEvents: 'none',
                      }}
                      role="img" aria-label={equippedPet.label}
                    >
                      {equippedPet.emoji}
                    </span>
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text, #111)' }}>{resolvedName}</div>
                </div>
              </div>

              {/* Stats */}
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                  <span style={{ color: 'var(--text-muted, #666)' }}>Level</span>
                  <span style={{ fontWeight: 600, color: 'var(--text, #111)' }}>{level}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                  <span style={{ color: 'var(--text-muted, #666)' }}>Lifetime XP</span>
                  <span style={{ fontWeight: 600, color: 'var(--text, #111)' }}>{lifetimeXP.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                  <span style={{ color: 'var(--text-muted, #666)' }}>Tier</span>
                  <span style={{ fontWeight: 600, color: 'var(--text, #111)' }}>{tierLabel}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                  <span style={{ color: 'var(--text-muted, #666)' }}>Plan</span>
                  <span style={{
                    fontWeight: 700, fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase',
                    padding: '2px 10px', borderRadius: '999px',
                    background: planTone.bg, color: planTone.fg, border: `1px solid ${planTone.bd}`,
                  }}>
                    {plan.label}
                  </span>
                </div>
              </div>

              {/* Display name editor */}
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted, #666)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Display Name
                </label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="text"
                    value={nameInput}
                    maxLength={60}
                    onChange={e => setNameInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveName()}
                    style={{
                      flex: 1, padding: '6px 8px', fontSize: '13px',
                      border: '1px solid var(--border, #ddd)', borderRadius: '6px',
                      background: 'var(--surface, #fff)', color: 'var(--text, #111)',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={nameSaving || !nameInput.trim() || nameInput.trim() === resolvedName}
                    style={{
                      padding: '6px 10px', fontSize: '12px', fontWeight: 600,
                      background: '#6366f1', color: '#fff', border: 'none',
                      borderRadius: '6px', cursor: 'pointer', flexShrink: 0,
                      opacity: (nameSaving || !nameInput.trim() || nameInput.trim() === resolvedName) ? 0.5 : 1,
                    }}
                  >
                    {nameSaving ? '…' : 'Save'}
                  </button>
                </div>
                {nameMsg && (
                  <div style={{ fontSize: '11px', marginTop: '4px', color: nameMsg.startsWith('✓') ? '#16a34a' : '#dc2626' }}>
                    {nameMsg}
                  </div>
                )}
              </div>

              {/* Appearance (accent colour) */}
              <div style={{ padding: '8px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
                <button
                  role="menuitem"
                  onClick={() => setAccentOpen(o => !o)}
                  aria-expanded={accentOpen}
                  style={{
                    width: '100%', padding: '9px 12px',
                    background: 'none', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', textAlign: 'left', fontSize: '13px',
                    fontWeight: 600, color: 'var(--text, #374151)',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover, #f3f4f6)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="8" cy="8" r="6.5" />
                    <path d="M8 1.5a4.5 4.5 0 0 0 0 13c1 0 1.2-.8.6-1.4a1.3 1.3 0 0 1 .9-2.2h1.3A2.7 2.7 0 0 0 13.5 8 6.5 6.5 0 0 0 8 1.5z" />
                  </svg>
                  Appearance
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                    style={{ marginLeft: 'auto', transform: accentOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                    <polyline points="4 6 8 10 12 6" />
                  </svg>
                </button>
                {accentOpen && (
                  <div style={{ padding: '10px 4px 2px', display: 'flex', gap: '10px' }}>
                    {ACCENTS.map(a => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => onAccentChange && onAccentChange(a.id)}
                        aria-label={`${a.label} accent${accent === a.id ? ', current' : ''}`}
                        aria-pressed={accent === a.id}
                        title={a.label}
                        style={{
                          width: '26px', height: '26px', borderRadius: '50%',
                          background: a.swatch, cursor: 'pointer',
                          border: accent === a.id ? '2px solid var(--text, #2a241d)' : '2px solid transparent',
                          boxShadow: accent === a.id ? '0 0 0 2px var(--surface, #fff)' : 'none',
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Change password */}
              <div style={{ padding: '8px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
                <button
                  role="menuitem"
                  onClick={() => { setPwOpen(o => !o); resetPwForm(); }}
                  aria-expanded={pwOpen}
                  style={{
                    width: '100%', padding: '9px 12px',
                    background: 'none', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', textAlign: 'left', fontSize: '13px',
                    fontWeight: 600, color: 'var(--text, #374151)',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover, #f3f4f6)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="7" width="10" height="7" rx="1.5" />
                    <path d="M5 7V4.5a3 3 0 0 1 6 0V7" />
                  </svg>
                  Change Password
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                    style={{ marginLeft: 'auto', transform: pwOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                    <polyline points="4 6 8 10 12 6" />
                  </svg>
                </button>
                {pwOpen && (
                  <div style={{ padding: '6px 4px 2px' }}>
                    <input type="password" autoComplete="current-password" placeholder="Current password"
                      value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} style={pwInputStyle} />
                    <input type="password" autoComplete="new-password" placeholder="New password (min 8 chars)"
                      value={pwNew} onChange={e => setPwNew(e.target.value)} style={pwInputStyle} />
                    <input type="password" autoComplete="new-password" placeholder="Confirm new password"
                      value={pwConfirm} onChange={e => setPwConfirm(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleChangePassword()} style={pwInputStyle} />
                    <button
                      onClick={handleChangePassword}
                      disabled={pwSaving || !pwCurrent || !pwNew || !pwConfirm}
                      style={{
                        width: '100%', padding: '7px 10px', fontSize: '12px', fontWeight: 700,
                        background: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px',
                        cursor: 'pointer',
                        opacity: (pwSaving || !pwCurrent || !pwNew || !pwConfirm) ? 0.5 : 1,
                      }}
                    >
                      {pwSaving ? 'Saving…' : 'Update password'}
                    </button>
                    {pwMsg && (
                      <div style={{ fontSize: '11px', marginTop: '6px', color: pwMsg.startsWith('✓') ? '#16a34a' : '#dc2626' }}>
                        {pwMsg}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Dev: Reset Progress */}
              <div style={{ padding: '8px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
                <button
                  role="menuitem"
                  onClick={handleResetProgress}
                  style={{
                    width: '100%', padding: '9px 12px',
                    background: 'none', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', textAlign: 'left', fontSize: '13px',
                    fontWeight: 600, color: '#d97706',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fffbeb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M1.5 8A6.5 6.5 0 1 0 3.5 3.5" />
                    <polyline points="1.5 1 1.5 4 4.5 4" />
                  </svg>
                  Reset My Progress
                </button>
              </div>

              {/* Logout */}
              <div style={{ padding: '8px' }}>
                <button
                  role="menuitem"
                  onClick={handleLogout}
                  style={{
                    width: '100%', padding: '9px 12px',
                    background: 'none', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', textAlign: 'left', fontSize: '14px',
                    fontWeight: 600, color: '#dc2626',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
                    <polyline points="11 11 14 8 11 5" />
                    <line x1="14" y1="8" x2="6" y2="8" />
                  </svg>
                  Sign Out
                </button>
              </div>

              {/* Legal / contact footer — open in a new tab so the app session stays put */}
              <div style={{
                padding: '10px 16px', borderTop: '1px solid var(--border, #e5e7eb)',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                gap: '8px', flexWrap: 'wrap', fontSize: '11px',
              }}>
                {[
                  { label: 'Privacy', href: '/privacy' },
                  { label: 'Terms', href: '/terms' },
                  { label: 'Contact', href: '/contact' },
                ].map((l, i) => (
                  <React.Fragment key={l.href}>
                    {i > 0 && <span style={{ color: 'var(--text-faint, #cbd5e1)' }}>·</span>}
                    <a href={l.href} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--text-muted, #888)', textDecoration: 'none', fontWeight: 600 }}>
                      {l.label}
                    </a>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>
    </header>
  );
}
